import React, { useState } from "react";
import { generateStory, illustrateStory, BACKEND } from "../lib/api";
import { LevelLanguage, Loader, SectionTitle } from "../components/Shared";
import { Sparkles, Image as ImageIcon, Share2, Check, MessageCircle } from "lucide-react";
import { useProfile } from "../context/ProfileContext";

const STARTERS = [
  "The Tortoise and the Hare",
  "The Lion and the Mouse",
  "Icarus and the wax wings",
  "Anansi outwits the sky god",
  "Pandora's box",
  "Rama's exile to the forest",
  "Newton's Disc as a fairy tale",
  "Persistence — story of the persistent ant",
];

export default function Stories() {
  const { profile, touch, incr } = useProfile();
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState(profile.level || "school");
  const [language, setLanguage] = useState(profile.language || "english");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [illusLoading, setIllusLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const go = async (t) => {
    const tt = (t ?? topic).trim();
    if (!tt) return;
    setLoading(true);
    setErr("");
    setData(null);
    setCopied(false);
    try {
      const res = await generateStory({ topic: tt, level, language });
      setData(res);
      touch();
      incr("storiesRead");
    } catch (e) {
      setErr("Could not generate story. Please try a different topic.");
    } finally {
      setLoading(false);
    }
  };

  const illustrate = async () => {
    if (!data?.id) return;
    setIllusLoading(true);
    try {
      const res = await illustrateStory(data.id);
      setData({ ...data, illustration_url: res.illustration_url });
    } catch (e) {
      setErr("Illustration failed. Try again.");
    } finally {
      setIllusLoading(false);
    }
  };

  const share = async () => {
    if (!data?.id) return;
    const url = `${BACKEND}/api/og/story/${data.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      window.prompt("Copy this link:", url);
    }
  };

  const shareWhatsApp = () => {
    if (!data?.id) return;
    const url = `${BACKEND}/api/og/story/${data.id}`;
    const text = `${data.title} — a story from EduSphere ✨\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const imgSrc = data?.illustration_url ? `${BACKEND}${data.illustration_url}` : null;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="stories-page">
      <SectionTitle kicker="Storytelling" title="Tales that teach." subtitle="Timeless stories from world mythology and folklore — each with a moral, a question to reflect on, and an optional AI illustration." />

      <div className="mb-5">
        <LevelLanguage level={level} setLevel={setLevel} language={language} setLanguage={setLanguage} />
      </div>

      <form
        className="flex gap-3 items-center"
        onSubmit={(e) => { e.preventDefault(); go(); }}
        data-testid="story-form"
      >
        <input
          className="input-field"
          placeholder="Type a topic — e.g. The Tortoise and the Hare, or 'persistence'"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          data-testid="story-input"
        />
        <button className="btn-primary inline-flex items-center gap-2 whitespace-nowrap" data-testid="story-generate" disabled={loading}>
          <Sparkles size={16} /> Tell story
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mt-4" data-testid="story-starters">
        {STARTERS.map((s, i) => (
          <button
            key={i}
            data-testid={`story-starter-${i}`}
            onClick={() => { setTopic(s); go(s); }}
            className="text-xs bg-white border border-[#DCD7CE] hover:border-[#E07A5F] px-3 py-1.5 rounded-full transition"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {loading && <Loader label="Weaving the story…" />}
        {err && <div className="text-[#D9534F] text-sm" data-testid="story-error">{err}</div>}
        {data && (
          <article className="card p-7 md:p-10 fadeIn" data-testid="story-output">
            {imgSrc && (
              <div className="mb-6 -mx-1 sm:-mx-2 rounded-2xl overflow-hidden border border-[#DCD7CE]">
                <img src={imgSrc} alt={data.title} className="w-full h-auto block" data-testid="story-illustration" />
              </div>
            )}
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-3">A story for you</div>
            <h2 className="font-story text-3xl md:text-4xl font-semibold text-[#1A1A1A] leading-tight">{data.title}</h2>
            <p className="font-story text-[19px] md:text-[21px] leading-[1.7] text-[#1A1A1A] mt-6 whitespace-pre-wrap">{data.story}</p>

            <div className="mt-6 flex flex-wrap gap-3" data-testid="story-actions">
              {!imgSrc && (
                <button
                  onClick={illustrate}
                  disabled={illusLoading}
                  className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="story-illustrate"
                >
                  <ImageIcon size={16} />
                  {illusLoading ? "Illustrating…" : "Generate illustration"}
                </button>
              )}
              <button onClick={share} className="btn-outline inline-flex items-center gap-2" data-testid="story-share">
                {copied ? <Check size={16} /> : <Share2 size={16} />}
                {copied ? "Link copied!" : "Share"}
              </button>
              <button
                onClick={shareWhatsApp}
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full px-5 py-2.5 font-semibold transition"
                data-testid="story-share-whatsapp"
                aria-label="Share on WhatsApp"
              >
                <MessageCircle size={16} /> WhatsApp
              </button>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <div className="bg-[#FBE7DF] rounded-2xl p-5 border border-[#DCD7CE]" data-testid="story-moral">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#E07A5F] font-semibold mb-2">Moral</div>
                <div className="text-[#1A1A1A]">{data.moral}</div>
              </div>
              <div className="bg-[#EAECE6] rounded-2xl p-5 border border-[#DCD7CE]" data-testid="story-reflection">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-2">Think with me</div>
                <div className="text-[#1A1A1A]">{data.reflection_question}</div>
              </div>
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
